import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_ORIGIN = "https://www.windroos.nl";
const DEFAULT_DESCRIPTION =
  "Ontdek hoe kinderen op De Windroos vanuit eigen ervaring en samen met anderen leren en groeien.";

const PUBLIC_ROUTES = {
  "/": ["De Windroos | Samen leren groeien", DEFAULT_DESCRIPTION],
  "/oudervereniging": [
    "Ouderparticipatie | De Windroos",
    "Lees hoe ouders bijdragen aan activiteiten, ervaringsgericht onderwijs en de schoolgemeenschap van De Windroos.",
  ],
  "/medezeggenschap": [
    "Medezeggenschap | De Windroos",
    "Bekijk hoe ouders en team meepraten met de schoolleiding en lees de notulen van de medezeggenschapsraad.",
  ],
  "/visie": [
    "Onderwijsvisie | De Windroos",
    "Ontdek hoe De Windroos vanuit de Freinet-visie kinderen nieuwsgierig maakt en ervaringen laat opdoen.",
  ],
  "/team": [
    "Team Windroos | De Windroos",
    "Maak kennis met de leerkrachten en onderwijsondersteuners van De Windroos en hun rollen binnen de school.",
  ],
  "/nieuw-schoolgebouw": [
    "Nieuw schoolgebouw | De Windroos",
    "Lees de bevestigde informatie en updates over het nieuwe kindcentrum voor De Windroos, De Werkschuit en Kind & Co Ludens in de Noorderwaard.",
  ],
  "/sticht": [
    "Het Sticht | De Windroos",
    "Lees over Het Sticht, de stichting achter De Windroos, en vind informatie over kwaliteit, klachten en beleid.",
  ],
  "/kalender": [
    "Jaarkalender | De Windroos",
    "Bekijk belangrijke momenten, studiedagen en activiteiten van De Windroos; komende gebeurtenissen staan bovenaan.",
  ],
  "/contact": [
    "Contact | De Windroos",
    "Neem contact op met De Windroos of kom langs aan de Karolingersweg in Wijk bij Duurstede.",
  ],
  "/groepen": [
    "Groepen | De Windroos",
    "Bekijk hoe kinderen van de kleuters tot groep 8 leren, samenwerken en steeds zelfstandiger worden.",
  ],
  "/groepen/kleuters": [
    "Kleuters (groep 1–2) | De Windroos",
    "Kleuters leren vanuit thema’s en werkhoeken, spelenderwijs en in overleg in de kring.",
  ],
  "/groepen/onderbouw": [
    "Onderbouw (groep 3–4) | De Windroos",
    "Ontdek hoe kinderen in de onderbouw lezen, rekenen en onderzoeken, met eigen teksten als uitgangspunt.",
  ],
  "/groepen/middenbouw": [
    "Middenbouw (groep 5–6) | De Windroos",
    "Bekijk hoe kinderen in de middenbouw werken met projecten, werkhoeken, eigen leervragen en zelfstandig plannen.",
  ],
  "/groepen/bovenbouw": [
    "Bovenbouw (groep 7–8) | De Windroos",
    "Ontdek hoe leerlingen in de bovenbouw verantwoordelijkheid nemen, uitdagingen aangaan en toewerken naar het voortgezet onderwijs.",
  ],
  "/nieuws": [
    "Nieuws | De Windroos",
    "Ontdek actuele verhalen en momenten uit de klas, de school en de omgeving van De Windroos.",
  ],
  "/nieuws/open-dag-19-juni": [
    "Open dag op vrijdag 19 juni | De Windroos",
    "Kijk terug op de open dag van De Windroos en maak kennis met onze lokalen en ons Freinet-onderwijs.",
  ],
  "/vacatures": [
    "Vacatures | De Windroos",
    "Bekijk actuele vacatures bij De Windroos en ontdek werken in een rijke leeromgeving met ruimte voor samenwerking.",
  ],
  "/aanmelden": [
    "Kennismaken | De Windroos",
    "Kies een open schooldag of vraag een persoonlijke kennismaking aan bij De Windroos. Formeel aanmelden volgt na de kennismaking.",
  ],
  "/documenten": [
    "Documenten | De Windroos",
    "Vind belangrijke documenten van De Windroos over onderwijs, ondersteuning, beleid en een veilige, gezonde school.",
  ],
};

const CANONICAL_ALIASES = {
  "/verhaalroute": "/",
  "/homepage": "/",
  "/ouders": "/#ouders",
  "/groepsouders": "/oudervereniging",
  "/gezondeschool": "/visie",
  "/freinet": "/#freinet",
  "/klas": "/#in-de-klas",
  "/onzeschool": "/#school",
  "/inhetnieuws": "/nieuws",
};

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
}

function ensureCanonical() {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  return element;
}

function RouteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
    const canonicalPath = CANONICAL_ALIASES[pathname] || pathname;
    const route = PUBLIC_ROUTES[canonicalPath.split("#")[0]];
    const isNotFound = !isAdmin && !route;
    const title = isAdmin
      ? "Beheer | De Windroos"
      : route?.[0] || "Pagina niet gevonden | De Windroos";
    const description = route?.[1] || DEFAULT_DESCRIPTION;
    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;

    document.title = title;
    ensureCanonical().setAttribute("href", canonicalUrl);
    ensureMeta('meta[name="description"]', { name: "description", content: description });
    ensureMeta('meta[name="robots"]', {
      name: "robots",
      content: isAdmin || isNotFound ? "noindex, nofollow" : "index, follow",
    });
    ensureMeta('meta[property="og:title"]', { property: "og:title", content: title });
    ensureMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    ensureMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary" });
  }, [pathname]);

  return null;
}

export default RouteMetadata;
